<template>
  <div class="container">
    <h5 class="first-heading">Minhas Contas</h5>
    <ul v-if="!userHasNoAccounts" class="account-list">
      <p class="instruction">Clique numa conta para geri-la.</p>
      <li
        @click="goToAccountTransactions"
        v-for="a in accounts"
        :key="a.id"
        :id="a.id"
        class="account-list-item"
      >
        <AppIcon @click="goToAccountTransactions" :id="a.id" :iconName="a.accountType.icon_name" />
        <span class="account-list-item-divider" :id="a.id">
          <span class="account-name" :id="a.id">{{ a.account_name }}</span>
          <span class="account-type" :id="a.id">{{ a.accountType.name }}</span>
          <span class="account-balance" :id="a.id">{{ Math.abs(a.current_balance).toFixed(2) }}
            {{ a.currency.short_name }}</span>
        </span>
      </li>
    </ul>
    <small v-if="userHasNoAccounts" class="message-info">Adicione hoje uma nova conta.</small>
    <AppButton
      @click.native="goToRoute"
      id="/contas/criar-conta"
      class="create-account-button"
      text="Adicionar nova Conta"
    />
    <h5 class="second-heading">Visão geral</h5>
    <p class="section-description">
      Esta corresponde a todas suas contas. Clique em uma conta acima para ver o histórico da mesma
      de forma detalhada.
    </p>
    <form>
      <label for="report-for-month">
        Mês e Ano
        <input
          @change="redrawCharts"
          v-model="selectedPeriod"
          type="month"
          name="report-for-month"
          id="report-for-month"
          required
        />
      </label>
    </form>
    <div class="account-report">
      <span class="account-report-title">Entradas e Saídas -
        {{formatTheDate(`${selectedPeriod}-01`)}}</span>
      <canvas class="chart i-and-e-chart">

      </canvas>
    </div>
    <div class="account-report">
      <span class="account-report-title">Entradas - {{formatTheDate(`${selectedPeriod}-01`)}}</span>
      <canvas class="chart i-chart">

      </canvas>
    </div>
    <div class="account-report">
      <span class="account-report-title">Saídas - {{formatTheDate(`${selectedPeriod}-01`)}}</span>
      <canvas class="chart e-chart">

      </canvas>
    </div>
    <TheBottomNav :linksList="linksList" @click.native="changeRoute"/>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import axios from "axios";
import { Chart, registerables, registerabregisterables } from "chart.js";
import AppIcon from "@/components/AppIcon.vue";
import AppButton from "@/components/AppButton.vue";
import TheBottomNav from "@/components/TheBottomNavbar.vue";
import { parseUserId, requestBuilder } from "../misc/util";

export default {
  name: "TheAccounts",
  components: {
    AppIcon,
    AppButton,
    TheBottomNav
  },
  beforeMount() {
    if (!this.isLoggedIn) this.$router.push({ name: "signin" });
  },
  created() {
    this.changeUserId(null);
    this.getUserAccounts();
    this.registerChartElements();
    this.getCurrentMonthAndYear();
  },
  mounted() {
    if (this.isLoggedIn) {
      setTimeout(() => {
        this.createLineChart();
        this.createIncomeChart();
        this.createExpenseChart();
      }, 500);
    }
  },
  data() {
    return {
      linksList: [
        {
          routeName: "accounts",
          name: "Contas",
          icon: "accounts",
          path: "/contas"
        },
        {
          routeName: "profile",
          name: "Definições",
          icon: "settings",
          path: "/perfil"
        }
      ],
      backgroundColorsForCharts: [
        "rgba(255, 99, 132, 0.6)",
        "rgba(54, 162, 235, 0.6)",
        "rgba(255, 206, 86, 0.6)",
        "rgba(75, 192, 192, 0.6)",
        "rgba(153, 102, 255, 0.6)",
        "rgba(255, 159, 64, 0.6)",
        "rgba(75, 192, 192, 0.8)"
      ],
      backgroundColorsForChartsScrambled: [
        "rgba(153, 102, 255, 0.6)",
        "rgba(54, 162, 235, 0.6)",
        "rgba(75, 192, 192, 0.6)",
        "rgba(255, 159, 64, 0.6)",
        "rgba(75, 192, 192, 0.8)",
        "rgba(255, 206, 86, 0.6)",
        "rgba(255, 99, 132, 0.6)"
      ],
      lineChartContext: null,
      lineChart: null,
      lineChartOptions: {
        responsive: true,
        plugins: {
          legend: {
            labels: {
              boxWidth: 10
            }
          },
          title: {
            display: false
          }
        },
        interaction: {
          mode: "index",
          intersect: false
        },
        scales: {
          y: {
            grid: {
              display: true
            }
          },
          x: {
            grid: {
              display: false,
            }
          }
        }
      },
      incomeChart: null,
      expenseChart: null,
      doughnutIncomeContext: null,
      doughnutExpenseContext: null,
      doughnutChartOptions: {
        responsive: true,
        plugins: {
          legend: {
            labels: {
              boxWidth: 10
            }
          },
          title: {
            display: false
          }
        }
      },
      accounts: [],
      selectedPeriod: null,
      isFetching: true,
      userDetails: {}
    };
  },
  computed: {
    ...mapGetters(["getUserId", "isLoggedIn"]),
    allAccountsOperations() {
      const arraysOfOperations = this.accounts.map(array => array.operations);
      const arrayOfOperations = [];
      arraysOfOperations.forEach(array => {
        arrayOfOperations.push(...array);
      });
      return arrayOfOperations;
    },
    userHasNoAccounts() {
      if (this.accounts.length > 0) return false;
      return true;
    },
    operationsToList() {
      const sortedOperations = this.sortArrayOfObjects(this.allAccountsOperations, "effective_by");
      const arrayOfDates = this.newArrayOf(sortedOperations, "effective_by");
      const nonDuplicates = this.removeDuplicates(arrayOfDates);
      const groupedOperations = this.groupOperationsByDate(nonDuplicates, sortedOperations);
      const filteredOperations = groupedOperations.filter(v => {
        const periodYear = new Date(this.selectedPeriod).getFullYear();
        const periodMonth = new Date(this.selectedPeriod).getMonth();

        const date = new Date(v.date);
        const mth = date.getMonth();
        const y = date.getFullYear();

        if (periodMonth === mth) {
          if (periodYear === y) {
            return true;
          }
        }
        return false;
      });
      return filteredOperations;
    },
    arraysIntoElements() {
      const arraysOfOperations = this.operationsToList.map(array => array.operationsWithSigns);
      const operations = [];
      arraysOfOperations.forEach(array => {
        operations.push(...array);
      });
      return operations;
    },
    dataForLineChartIncomeDataset() {
      const data = [];
      this.labelsForLineChart.forEach(l => {
        this.operationsToList.forEach(o => {
          const dayOfTheOperation = new Date(o.date).getDate();
          if (dayOfTheOperation === l) {
            const operations = o.operationsWithSigns.map(op => op)
              .filter(op => op.operation_type === "income")
              .map(op => op.amount);

            if (operations.length > 0) {
              const total = operations.reduce((acc, val) => acc + val, 0).toFixed(2);
              data.push(total);
            } else data.push(0);
          }
        });
      });
      return data;
    },
    dataForLineChartExpenseDataset() {
      const data = [];
      this.labelsForLineChart.forEach(l => {
        this.operationsToList.forEach(o => {
          const dayOfTheOperation = new Date(o.date).getDate();
          if (dayOfTheOperation === l) {
            const operations = o.operationsWithSigns.map(op => op)
              .filter(op => op.operation_type === "expense")
              .map(op => op.amount);

            if (operations.length > 0) {
              const total = operations.reduce((acc, val) => acc + val, 0).toFixed(2);
              data.push(total);
            } else data.push(0);
          }
        });
      });
      return data;
    },
    dataForDoughnutIncomeChart() {
      const allOperations = this.arraysIntoElements.filter(el => el.operation_type === "income");
      const operationTypes = this.removeDuplicates(this.arraysIntoElements.filter(o => o.operation_type === "income")
        .map(op => op.operationCategory.name));
      const groupedOperationsByType = operationTypes.map(ot => {
        const operationsOfThisType = allOperations.filter(op => op.operationCategory.name === ot);
        const total = operationsOfThisType.reduce((acc, op) => acc + op.amount, 0);

        return {
          operationType: ot,
          total
        };
      });
      return groupedOperationsByType;
    },
    dataForDoughnutExpenseChart() {
      const allOperations = this.arraysIntoElements.filter(el => el.operation_type === "expense");
      const operationTypes = this.removeDuplicates(this.arraysIntoElements.filter(o => o.operation_type === "expense")
        .map(op => op.operationCategory.name));
      const groupedOperationsByType = operationTypes.map(ot => {
        const operationsOfThisType = allOperations.filter(op => op.operationCategory.name === ot);
        const total = operationsOfThisType.reduce((acc, op) => acc + op.amount, 0);

        return {
          operationType: ot,
          total
        };
      });
      return groupedOperationsByType;
    },
    labelsForLineChart() {
      return this.operationsToList.map(o => new Date(o.date).getDate());
    },
    labelsForDoughnutIncomeChart() {
      return this.dataForDoughnutIncomeChart.map(go => go.operationType);
    },
    labelsForDoughnutExpenseChart() {
      return this.dataForDoughnutExpenseChart.map(go => go.operationType);
    },
  },
  methods: {
    ...mapActions(["changeUserId"]),
    registerChartElements() {
      Chart.register(...registerables);
      Chart.defaults.font.family = "Open Sans Regular";
    },
    createLineChart() {
      setTimeout(() => {
        const el = document.querySelector(".i-and-e-chart");
        if (el) {
          this.lineChartContext = el.getContext("2d");
          if (this.lineChartContext) {
            if (this.operationsToList.length > 0) {
              const chartData = () => {
                const labels = this.labelsForLineChart;

                const datasets = [
                  {
                    label: "Entrada",
                    fill: true,
                    backgroundColor: ["#25A0A4"],
                    data: this.dataForLineChartIncomeDataset,
                    tension: 0.4,
                    pointBorderWidth: 0.3
                  },
                  {
                    label: "Saida",
                    fill: true,
                    backgroundColor: ["#D52941"],
                    data: this.dataForLineChartExpenseDataset,
                    tension: 0.4,
                    pointBorderWidth: 0.3
                  }
                ];

                return {
                  labels,
                  datasets
                };
              };

              const configuration = (data) => ({ type: "line", data, options: this.lineChartOptions });

              this.lineChart = new Chart(this.lineChartContext, configuration(chartData()));
            }
          }
        }
      }, 1000);
    },
    createIncomeChart() {
      setTimeout(() => {
        const el = document.querySelector(".i-chart");
        if (el) {
          this.doughnutIncomeContext = el.getContext("2d");
          if (this.doughnutIncomeContext) {
            if (this.operationsToList.length > 0) {
              const chartData = () => {
                const labels = this.labelsForDoughnutIncomeChart;

                const datasets = [
                  {
                    label: "Entradas",
                    data: this.dataForDoughnutIncomeChart.map(go => go.total),
                    backgroundColor: this.backgroundColorsForCharts,
                    hoverOffset: 0
                  }
                ];

                return {
                  labels,
                  datasets
                };
              };

              const configuration = (data) => ({ type: "doughnut",
                data,
                options: this.doughnutChartOptions });

              this.incomeChart = new Chart(this.doughnutIncomeContext, configuration(chartData()));
            }
          }
        }
      }, 1000);
    },
    createExpenseChart() {
      setTimeout(() => {
        const el = document.querySelector(".e-chart");
        if (el) {
          this.doughnutExpenseContext = el.getContext("2d");
          if (this.doughnutExpenseContext) {
            if (this.operationsToList.length > 0) {
              const chartData = () => {
                const labels = this.labelsForDoughnutExpenseChart;

                const datasets = [
                  {
                    label: "Saídas",
                    data: this.dataForDoughnutExpenseChart.map(go => go.total),
                    backgroundColor: this.backgroundColorsForChartsScrambled,
                    hoverOffset: 0
                  }
                ];

                return {
                  labels,
                  datasets
                };
              };

              const configuration = (data) => ({ type: "doughnut",
                data,
                options: this.doughnutChartOptions });

              this.expenseChart = new Chart(this.doughnutExpenseContext,
                configuration(chartData()));
            }
          }
        }
      }, 1000);
    },
    updateLineChart() {
      const lineChart = Chart.getChart(this.lineChartContext);
      if (lineChart) {
        lineChart.data.labels = this.labelsForLineChart;
        lineChart.data.datasets[0].data = this.dataForLineChartIncomeDataset;
        lineChart.data.datasets[1].data = this.dataForLineChartExpenseDataset;
        lineChart.update();
      } else this.createLineChart();
    },
    updateDoughnutIncomeChart() {
      const doughnutIncomeChart = Chart.getChart(this.doughnutIncomeContext);
      if (doughnutIncomeChart) {
        doughnutIncomeChart.data.labels = this.labelsForDoughnutIncomeChart;
        doughnutIncomeChart.data.datasets[0].data = this.dataForDoughnutIncomeChart
          .map(dfi => dfi.total);
        doughnutIncomeChart.update();
      } else this.createIncomeChart();
    },
    updateDoughnutExpenseChart() {
      const doughnutExpenseChart = Chart.getChart(this.doughnutExpenseContext);
      if (doughnutExpenseChart) {
        doughnutExpenseChart.data.labels = this.labelsForDoughnutExpenseChart;
        doughnutExpenseChart.data.datasets[0].data = this.dataForDoughnutExpenseChart
          .map(dfe => dfe.total);
        doughnutExpenseChart.update();
      } else this.createExpenseChart();
    },
    redrawCharts() {
      this.updateLineChart();
      this.updateDoughnutIncomeChart();
      this.updateDoughnutExpenseChart();
    },
    getCurrentMonthAndYear() {
      const year = new Date().getFullYear();
      const month = `0${new Date().getMonth() + 1}`;
      if (month.length === 3) {
        const fixedMonth = month.slice(1, 3);
        this.selectedPeriod = `${year}-${fixedMonth}`;
      } else this.selectedPeriod = `${year}-${month}`;
    },
    formatTheDate(date, formatOption) {
      const defaultOption = { year: "numeric", month: "long" };
      if (formatOption === undefined) {
        const formattedDate = new Date(date).toLocaleDateString("pt-PT", defaultOption);
        const firstChar = formattedDate.charAt(0).toUpperCase();
        const slicedFormattedDate = formattedDate.slice(1);
        return `${firstChar}${slicedFormattedDate}`;
      }
      const formattedDate = new Date(date).toLocaleDateString("pt-PT", formatOption);
      const firstChar = formattedDate.charAt(0).toUpperCase();
      const slicedFormattedDate = formattedDate.slice(0, 1);
      return `${firstChar}${slicedFormattedDate}`;
    },
    goToRoute(e) {
      const nextRoute = e.srcElement.id;
      const currentRoute = this.$route.path;

      if (currentRoute !== nextRoute) this.$router.push({ path: nextRoute });
    },
    goToAccountTransactions(e) {
      if (e.srcElement.id) {
        const { id } = e.srcElement;
        const accountData = this.accounts.filter(a => a.id === parseInt(id, 10))[0];
        const nextRoute = `/contas/${id}/transaccoes`;
        const currentRoute = this.$route.path;

        if (nextRoute !== currentRoute) {
          this.$router.push({ name: "accountTransactions",
            params: {
              accountID: id, accountData } });
        }
      }
    },
    changeRoute(e) {
      if (e.srcElement.id) {
        const { srcElement: { id } } = e;
        if (this.$route.name !== id) {
          this.$router.push({ name: id,
            params: { userDetails: this.userDetails } });
        }
      }
    },
    async getUserAccounts() {
      this.isFetching = true;
      const url = "/accounts";
      const method = "GET";
      const requestOptions = requestBuilder(url, method);
      const userId = parseUserId();
      const secondURL = `/users/${userId}`;
      const secondRequestOptions = requestBuilder(secondURL, method);

      try {
        const { data } = await axios.request(requestOptions);
        this.accounts = data;
        this.isFetching = false;
      } catch (error) {
        this.isFetching = false;
      }

      try {
        const { data } = await axios.request(secondRequestOptions);
        this.userDetails = data;
      } catch (error) {
        console.log(error);
      }
    },
    sortArrayOfObjects(array, attribute) {
      const arrayCopy = [...array];
      return arrayCopy.sort((a, b) => {
        if (a[attribute] < b[attribute]) return 1;
        if (a[attribute] > b[attribute]) return -1;
        return 0;
      });
    },
    newArrayOf(array, attribute) {
      return array.map(v => v[attribute]);
    },
    customIndexOf(array, value) {
      const indexOf = [];
      array.forEach((el, index) => {
        if (el === value) {
          indexOf.push(index);
        }
      });
      const [firstOcurrence] = indexOf;
      return firstOcurrence;
    },
    removeDuplicates(array) {
      return this.sortArrayOfObjects(array).filter(
        (el, index, originalArray) => this.customIndexOf(originalArray, el) === index
      );
    },
    groupOperationsByDate(arrayOfDates, arrayOfOperations) {
      const groupedOperationsByDate = arrayOfDates.map(d => {
        const operationsOnThisDate = arrayOfOperations.filter(v => v.effective_by === d);

        const operationsWithSigns = operationsOnThisDate.map(v => {
          const newAmount = v.operation_type === "expense" ? -Math.abs(v.amount) : v.amount;

          const operation = v;
          operation.amount = newAmount;

          return operation;
        });

        const total = operationsWithSigns.reduce((acc, v) => acc + v.amount, 0);

        return {
          date: d,
          total,
          operationsWithSigns
        };
      });

      return groupedOperationsByDate;
    }
  }
};
</script>

<style scoped>
form {
  margin-bottom: 1.6rem;
}

form label {
  color: var(--color-tertiary);
  width: 100%;
}

form label input {
  width: 100%;
}

.first-heading {
  margin-bottom: 1.6rem;
}

.container {
  display: flex;
  flex-direction: column;
  padding-bottom: 5rem;
}

.account-list {
  display: flex;
  flex-direction: column;
}

.account-list .instruction {
  margin-bottom: 0.6rem;
  font-size: 1rem;
  color: var(--color-secondary);
}

.account-list-item {
  margin-bottom: 1rem;
  background-color: var(--color-white);
  border-radius: 10px;
  padding: 0.8rem 0 0.8rem 2rem;
  display: flex;
  align-items: center;
  filter: drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.15));
  cursor: pointer;
}

.account-list-item svg {
  margin-right: 2rem;
}

.account-list-item :last-child {
  margin-bottom: 0;
}

.account-list-item-divider {
  display: flex;
  flex-direction: column;
}

.account-type {
  color: var(--color-tertiary);
}

.account-name,
.account-type {
  font-size: 0.75rem;
}

.account-balance {
  color: var(--color-secondary);
}

.create-account-button {
  background-color: var(--color-primary);
  color: var(--color-quaternary);
  margin: 1.6rem auto 2rem auto;
}

.section-description {
  font-size: 0.75rem;
  color: var(--color-tertiary);
  margin-bottom: 1.6rem;
}

.account-report {
  display: flex;
  flex-direction: column;
  position: relative;
  background-color: var(--color-white);
  width: 100%;
  padding: 1rem 0.8rem 1rem 0.8rem;
  margin-bottom: 1.4rem;
  border-radius: 10px;
}

.account-report:last-child {
  margin-bottom: 2.4rem;
}

.account-report-title {
  color: var(--color-tertiary);
  font-size: 0.85rem;
}

/* .report-button {
  background-color: var(--color-secondary);
  color: var(--color-quaternary);
  margin: 0 auto 2rem auto;
} */

.message-info {
  text-align: center;
  color: var(--color-secondary);
}
</style>
